import React, { useState, useCallback } from "react";
import styled from "@emotion/styled";

const Select = ({
  label,
  options,
  value,
  onChange,
  placeholder = "Select",
  required = false,
  name,
  className,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = useCallback(
    (option) => {
      onChange?.({ target: { name, value: option.value } });
      setIsOpen(false);
    },
    [onChange, name]
  );

  const toggleDropdown = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  }, [disabled]);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <SelectContainer className={className}>
      <Label>{label}</Label>
      <SelectButton
        onClick={toggleDropdown}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        role="combobox"
      >
        <SelectValue isPlaceholder={!selectedOption}>
          {selectedOption ? selectedOption.label : placeholder}
        </SelectValue>
        <CaretIcon>
          <svg
            width="12"
            height="7"
            viewBox="0 0 12 7"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.28125 5.71875L1.28125 1.71875C1 1.4375 0.90625 1 1.0625 0.625C1.21875 0.25 1.59375 0 2 0H10C10.4062 0 10.75 0.25 10.9062 0.625C11.0625 1 10.9688 1.4375 10.6875 1.71875L6.6875 5.71875C6.3125 6.125 5.65625 6.125 5.28125 5.71875Z"
              fill="#64748B"
            />
          </svg>
        </CaretIcon>
      </SelectButton>
      {isOpen && (
        <OptionsMenu role="listbox">
          {options.map((option) => (
            <OptionItem
              key={option.value}
              isSelected={value === option.value}
              onClick={() => handleSelect(option)}
              role="option"
              aria-selected={value === option.value}
            >
              <OptionText isSelected={value === option.value}>
                {option.label}
              </OptionText>
            </OptionItem>
          ))}
        </OptionsMenu>
      )}
    </SelectContainer>
  );
};

const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  position: relative;
`;

const Label = styled.label`
  color: rgb(33, 37, 67);
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: normal;
`;

const SelectButton = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 16px;
  height: 42px;
  background: #ffffff;
  border: 1px solid rgb(224, 228, 244);
  border-radius: 8px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: rgb(97, 98, 197);
  }

  &:focus {
    outline: none;
    border-color: rgb(97, 98, 197);
  }
`;

const SelectValue = styled.span`
  color: ${(props) => (props.isPlaceholder ? "#000000" : "#000000")};
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: normal;
`;

const CaretIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OptionsMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.1);
  padding: 8px;
  z-index: 10;
  max-height: 300px;
  overflow-y: auto;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  border-radius: 8px;
  background: ${(props) => (props.isSelected ? "#F1F5F9" : "transparent")};
  transition: background-color 0.2s ease;

  &:hover {
    background: #f1f5f9;
  }
`;

const OptionText = styled.span`
  color: ${(props) => (props.isSelected ? "#6162C5" : "#000000")};
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: normal;
`;

export default Select;
